import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { memo, useEffect, useRef, useState } from 'react';
import { Toolbar } from '../Toolbar/Toolbar';
import DayHeader from '../DayHeader/DayHeader';
import SingleEvent from '../SingleEvent/SingleEvent';
import Legend from '../Legend/Legend';
import { CalendarContainer, CalendarWrapper, Container, LegendWrapper } from './CalendarStyle';
import { ACCOUNT_FIRST_DAY } from '../../enums/account-first-day';
import { Event } from '../../interfaces/event';
import { Settings } from '../../interfaces/settings';

interface CalendarPropsType {
  settings: Settings;
  events: Event[];
}

const Calendar = memo(({ settings, events }: CalendarPropsType) => {
  const calendarRef = useRef<FullCalendar>(null);

  const [isRefReady] = useState(false);
  const [view, setView] = useState('timeGridWeek');
  const [title, setTitle] = useState('');
  const [colorBylegend, setColorByLegend] = useState<{ color: string; value: any }[]>([]);
  const [groupLegend, setGroupLegend] = useState<{ color: string; value: any }[]>([]);

  const accountFirstDay = 'sunday';

  const isLegend =
    (settings.board.color_by ||
      settings.subitems_board?.color_by ||
      settings.group_selection.color) &&
    settings.legend
      ? true
      : false;

  useEffect(() => {
    if (settings.legend) {
      if (settings.board.color_by || settings.subitems_board?.color_by) {
        const uniqueColors = new Set();
        const uniqueColorsArray = [...(events as any[])]
          .map((event) => ({
            color: event?.data?.colorBy?.color || event.backgroundColor,
            value: event?.data?.colorBy?.value || '',
          }))
          .filter((color) => {
            if (uniqueColors.has(color.color)) return false;
            uniqueColors.add(color.color);
            return true;
          });
        setColorByLegend(uniqueColorsArray);
      }
      if (settings.group_selection.color) {
        const uniqueColors = new Set();
        const uniqueColorsArray = [...events]
          .map((event) => ({
            color: event?.data?.group?.color,
            value: event?.data?.group?.title,
          }))
          .filter((color) => {
            if (uniqueColors.has(color.color)) return false;
            uniqueColors.add(color.color);
            return true;
          });
        setGroupLegend(uniqueColorsArray);
      }
    }
  }, [
    settings.board.color_by,
    settings.group_selection,
    settings.legend,
    settings.subitems_board?.color_by,
    events,
  ]);

  useEffect(() => {
    queueMicrotask(() => {
      settings.is_initial_date &&
        calendarRef?.current
          ?.getApi()
          .gotoDate(
            settings.initial_timestamp !== 0 ? settings.initial_timestamp : new Date().getTime()
          );
    });
  }, [settings.is_initial_date, settings.initial_timestamp]);

  return (
    <Container $isGrid={settings.grid}>
      <Toolbar
        calendarRef={calendarRef}
        isRefReady={isRefReady}
        view={view}
        defaultView={settings.default_view}
        setView={setView}
        title={title}
      />
      <CalendarContainer id="divToPrint">
        <CalendarWrapper $isLegend={isLegend}>
          <FullCalendar
            navLinks={true}
            navLinkDayClick={(props, view) => {
              calendarRef.current?.getApi().changeView('timeGridDay', props);
              setView('timeGridDay');
            }}
            ref={calendarRef}
            forceEventDuration={true}
            selectable={false}
            editable={false}
            nowIndicator={true}
            droppable={false}
            plugins={[
              timeGridPlugin,
              dayGridPlugin,
              multiMonthPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            datesSet={(arg) => setTitle(arg.view.title)}
            initialView={settings.default_view}
            hiddenDays={
              settings.work_week
                ? accountFirstDay === ACCOUNT_FIRST_DAY.SUNDAY
                  ? [5, 6]
                  : [0, 6]
                : []
            }
            headerToolbar={false}
            eventDragMinDistance={8}
            slotDuration={settings.slot_duration}
            firstDay={settings?.first_day || 0}
            slotLabelInterval={'01:00'}
            multiMonthMaxColumns={4}
            multiMonthMinWidth={530}
            dayMaxEventRows={settings.is_collapse_all_day_events ? 4 : false}
            snapDuration={'00:15'}
            slotLabelFormat={{
              hour: 'numeric',
              ...(!settings.hour12 && { minute: '2-digit' }),
              hour12: settings.hour12,
            }}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              hour12: settings.hour12,
              meridiem: 'short',
            }}
            height={'100%'}
            slotMinTime={`${settings.start_time || '08'}:00:00`}
            slotMaxTime={`${settings.end_time || '19'}:00:00`}
            scrollTimeReset={false}
            dayHeaderContent={(props) => <DayHeader {...props} />}
            allDayContent={(_props) => (
              <div style={{ width: '50px', textAlign: 'center', fontSize: '12px' }}>all-day</div>
            )}
            eventContent={(props) => (
              <SingleEvent
                {...props}
                key={props.event.id}
                isAllDayEventData={settings.is_all_day_data}
                groupSelection={settings.group_selection}
              />
            )}
            events={events}
          />
        </CalendarWrapper>
        {settings.legend &&
          (settings.board.color_by ||
            settings.subitems_board?.color_by ||
            settings.group_selection.color) && (
            <LegendWrapper>
              {(settings.board.color_by || settings.subitems_board?.color_by) && (
                <Legend legend={colorBylegend} />
              )}
              {settings.group_selection.color && (
                <Legend legend={groupLegend} colorRadius={'2px'} />
              )}
            </LegendWrapper>
          )}
      </CalendarContainer>
    </Container>
  );
});

export default Calendar;
